import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { create } from 'zustand'
import { CheckCircle2 } from 'lucide-react'
import type { Alert, AlertList } from '@one-deployment/api'
import { Button, PageHeader, Segmented, SkeletonRows, Table, Td, Tr } from '@/components/ui'
import { Button as SButton } from '@/components/shadcn/button'
import { Age, EmptyState, ErrorState } from '@/components/status'
import { enc, get, post } from '@/lib/api'
import { cn } from '@/lib/cn'
import { dateTime } from '@/lib/format'
import { useGlobalQueryOpts } from '@/lib/live'
import { qk } from '@/lib/query'

const DISMISSED_KEY = 'alerts-dismissed'
const load = (): Set<string> => { try { return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? '[]') as string[]) } catch { return new Set() } }

/**
 * Dismissed attention alerts, kept in this browser. An alert has a new id each
 * time it opens, so dismissing one hides only this opening: if the problem
 * comes back, so does the alert. `down` cannot be dismissed at all.
 */
export const useDismissed = create<{ ids: Set<string>; dismiss: (id: string) => void }>((set) => ({
  ids: load(),
  dismiss: (id) => {
    set((s) => {
      const ids = new Set(s.ids).add(id)
      // Bounded: ids of alerts long cleared are dead weight.
      localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids].slice(-200)))
      return { ids }
    })
    // The Worker only checks the alert may be dismissed; the hiding stays here.
    void post(`/api/alerts/${enc(id)}/dismiss`).catch(() => undefined)
  },
}))

/** The open alerts this person has not dismissed: what the counters count. */
export function useVisibleAlerts<T extends { id: string }>(alerts: T[]): T[] {
  const ids = useDismissed((s) => s.ids)
  return alerts.filter((a) => !ids.has(a.id))
}

export function AlertRow({ alert, compact }: { alert: Alert; compact?: boolean }) {
  const dismissed = useDismissed((s) => s.ids.has(alert.id))
  const dismiss = useDismissed((s) => s.dismiss)
  if (dismissed) return null
  const down = alert.level === 'down'
  return (
    <div className={cn('flex items-start gap-2.5 border-b border-border px-3 last:border-0', compact ? 'py-2' : 'py-2.5')}>
      <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', down ? 'bg-err' : 'border-[1.5px] border-warn')} aria-label={down ? 'down' : 'attention'} />
      <div className="min-w-0 flex-1">
        <div className="text-sm text-fg">{alert.what}</div>
        <div className="text-xs text-fg-subtle">todo: {alert.todo}</div>
      </div>
      <Age from={alert.openedAt} className="text-xs text-fg-subtle" />
      {!down && (
        <SButton variant="link" className="h-auto p-0 text-2xs font-normal text-fg-faint hover:text-fg" onClick={() => dismiss(alert.id)} title="Hide this alert; it comes back if the problem does">
          dismiss
        </SButton>
      )}
    </div>
  )
}

export function AlertsPage() {
  const [tab, setTab] = useState<'open' | 'cleared'>('open')
  // The global topic invalidates this; the poll is the fallback.
  const live = useGlobalQueryOpts(60_000)
  const { data, error, refetch } = useQuery({
    queryKey: qk.alerts(),
    queryFn: ({ signal }) => get<AlertList>('/api/alerts', signal),
    ...live,
  })
  const dismissed = useDismissed((s) => s.ids)
  const dismiss = useDismissed((s) => s.dismiss)
  const rows = (data?.alerts ?? []).filter((a) => (tab === 'cleared' ? a.clearedAt != null : a.clearedAt == null && !dismissed.has(a.id)))

  return (
    <div>
      <PageHeader title="Alerts" meta="Checked every minute by the Worker; one email when an alert opens and one when it clears.">
        <div>
          <Segmented value={tab} onChange={setTab} options={[{ value: 'open', label: 'Open' }, { value: 'cleared', label: 'Cleared 7d' }]} />
        </div>
      </PageHeader>
      {error && !data ? (
        <ErrorState error={error} retry={() => void refetch()} />
      ) : !data ? (
        <SkeletonRows rows={5} />
      ) : rows.length === 0 ? (
        <EmptyState icon={<CheckCircle2 size={20} className="text-ok" />} title={tab === 'open' ? 'All clear' : 'Nothing cleared in the last 7 days'} body="Checked every minute." />
      ) : (
        <Table head={['Level', 'What', 'Todo', 'Subject', tab === 'open' ? 'Since' : 'Open → cleared', '']}>
          {rows.map((a) => (
            <Tr key={a.id}>
              <Td className={a.level === 'down' ? 'text-err' : 'text-warn'}>{a.level === 'down' ? '● down' : '◐ attention'}</Td>
              <Td className="max-w-md truncate">{a.what}</Td>
              <Td className="max-w-sm truncate text-fg-subtle">{a.todo}</Td>
              <Td mono className="text-fg-subtle">{a.subject}</Td>
              <Td className="text-fg-subtle">{tab === 'open' ? <Age from={a.openedAt} /> : `${dateTime(a.openedAt)} → ${a.clearedAt ? dateTime(a.clearedAt) : '—'}`}</Td>
              <Td>{tab === 'open' && a.level === 'attention' && <Button size="sm" variant="ghost" onClick={() => dismiss(a.id)}>Dismiss</Button>}</Td>
            </Tr>
          ))}
        </Table>
      )}
    </div>
  )
}
